import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { MIN_N } from "@/lib/checkpointState";
import { QueryBoundary, retryTransient } from "@/lib/queryState";
import { CostOfNoPanel } from "@/components/desk/CostOfNo";
import { PaperStrategiesPanel } from "@/components/desk/PaperStrategies";
import { TenAmRecordPanel } from "@/components/desk/TenAmRecord";
import { Badge, PageHead, Panel, RuleResult, StatCell, EmptyState, fmt, sessionDateLabel, etAndLocal } from "@/sentinel";

const pts = (v) => fmt(v, "pts", { signed: true });

/** A yes/no check reads as a validation result: glyph + label, never a colour alone. */
function YesNo({ v }) {
  if (v === null || v === undefined) return <RuleResult result="NA">—</RuleResult>;
  return v ? <RuleResult result="PASS">YES</RuleResult> : <RuleResult result="FAIL">NO</RuleResult>;
}

const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(0)}%`);

/** A labelled section of the page: the title says which window the panels below it cover. */
function SectionHead({ title, sub, testId }) {
  return (
    <div className="sn-stack" style={{ gap: 2 }} data-testid={testId}>
      <span className="sn-kicker" role="heading" aria-level={2}>{title}</span>
      {sub && <span className="sn-small sn-muted">{sub}</span>}
    </div>
  );
}

/** Accuracy over all live sessions. The sample is sessions (days), not runs: the five runs of one day
 *  move together, so n counts distinct session dates. Older backends send only `checkpoints_scored`. */
function Diagnostics({ diag }) {
  const p = diag?.payload || {};
  const runs = p.runs_scored ?? p.checkpoints_scored;
  const sessions = p.sessions_scored;
  const n = sessions ?? runs;
  const unit = sessions != null ? "sessions" : "runs";
  const small = n == null || n < MIN_N;
  const sample = sessions != null ? `n = ${sessions} sessions (${runs ?? "—"} runs)` : `n = ${runs ?? "—"} runs`;
  const afterOpen = p.direction_after_open_runs || [3, 4, 5];
  const afterOpenLabel = afterOpen.length > 1 ? `Runs ${afterOpen[0]}-${afterOpen[afterOpen.length - 1]} are` : `Run ${afterOpen[0]} is`;
  return (
    <Panel title="Forecast accuracy · all live sessions" icon="Sigma" data-testid="diagnostics-panel"
      meta={<Badge icon={small ? "FlaskConical" : "Sigma"} className={small ? "sn-prov" : undefined} data-testid="diagnostics-sample">{sample}</Badge>}>
      <div className="sn-stack">
        {small && <p className="sn-small" style={{ margin: 0, fontWeight: 600 }} data-testid="diagnostics-sample-note">Fewer than {MIN_N} {unit}: too early to read as a result.</p>}
        <div className="sn-row" style={{ gap: "var(--space-8)", flexWrap: "wrap" }}>
          <StatCell size={small ? "sm" : "md"} label={sessions != null ? "Sessions scored" : "Runs scored"} value={n == null ? "—" : String(n)}
            sub={sessions != null ? `${runs ?? "—"} runs, about five a day` : undefined} />
          <StatCell size={small ? "sm" : "md"} label="q10–q90 coverage" value={pct(p.coverage_rate)}
            sub={`target about ${pct(p.coverage_target ?? 0.8)}`} data-testid="diagnostics-coverage" />
          <StatCell size={small ? "sm" : "md"} label="Direction accuracy" value={pct(p.direction_accuracy)}
            sub={`chance = ${pct(p.direction_chance ?? 0.5)}`} data-testid="diagnostics-direction" />
        </div>
        <p className="sn-small sn-faint" style={{ margin: 0 }} data-testid="diagnostics-after-open-note">
          {afterOpenLabel} scored after the open, so direction is partly known.
        </p>
      </div>
    </Panel>
  );
}

export default function Scoring() {
  const { selectedDate } = useAuth();
  const query = useQuery({ queryKey: ["scores", selectedDate], queryFn: () => api.getScores(selectedDate), enabled: !!selectedDate, retry: retryTransient });
  const diag = query.data?.diagnostics;

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="scoring-page">
      <PageHead
        crumb={["Evidence", <span className="sn-sans-num">{sessionDateLabel(selectedDate)}</span>]}
        title={<span data-testid="scoring-title">Scoring and accuracy</span>}
        context="The 10:00 am programme first, then the old rule set's post-close review · nothing here was traded"
      />

      <section className="sn-stack" style={{ gap: "var(--space-4)" }} aria-label="The 10:00 am programme" data-testid="scoring-ten-am">
        <SectionHead title="The 10:00 am programme" testId="scoring-ten-am-head"
          sub={`The main research line: one entry time, ${etAndLocal("10:00")}. The frozen quiet-day filter's forward record.`} />
        <TenAmRecordPanel day={selectedDate} />
      </section>

      <SectionHead title="Old rule set (no edge found)" testId="scoring-old-rules-head"
        sub="The rules the desk was first built on, scored at Runs 1 to 5 (08:40 to 10:01). No test found an edge after costs; they are kept for comparison, and the paper tests below run to their review dates." />

      <section className="sn-stack" style={{ gap: "var(--space-4)" }} aria-label="This session" data-testid="scoring-this-session">
        <SectionHead title="This session" testId="scoring-this-session-head"
          sub={<>Forecasts for <span className="sn-sans-num">{sessionDateLabel(selectedDate)}</span> against what the market did. Nothing here was traded.</>} />

        <QueryBoundary query={query} loading="page" what={`the scores for ${selectedDate}`} errorTestId="scoring-error"
          isEmpty={(d) => !(d?.scores || []).length}
          empty={{ icon: "Target", title: "Not scored yet", body: "Run 6 (post-close review) scores each run once the session's OHLC is recorded.", testId: "scoring-empty" }}>
          {(data) => {
            const scores = data.scores || [];
            const outcomes = data.trade_outcomes || [];
            return (
              <>
                <Panel title="Forecast error by run" icon="Target" flush meta={<span>index points · actual − forecast</span>}>
                  <div style={{ overflowX: "auto" }}>
                    <table className="sn-table" data-testid="scores-table">
                      <thead>
                        <tr><th>Run</th><th className="r">Open err</th><th className="r">High err</th><th className="r">Low err</th><th className="r">Close q50 err</th><th className="r">Range err</th><th>q10–q90 coverage</th><th>Direction</th></tr>
                      </thead>
                      <tbody>
                        {scores.map((s) => (
                          <tr key={s.id} data-testid={`score-run-${s.run}`}>
                            <td className="id">R{s.run}</td>
                            <td className="num">{pts(s.errors?.open_error)}</td>
                            <td className="num">{pts(s.errors?.high_error)}</td>
                            <td className="num">{pts(s.errors?.low_error)}</td>
                            <td className="num">{pts(s.errors?.close_q50_error)}</td>
                            <td className="num">{pts(s.errors?.expected_range_error)}</td>
                            <td><YesNo v={s.coverage_q10_q90} /></td>
                            <td><YesNo v={s.direction_accuracy} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Panel>

                <Panel title={<span data-testid="hypothetical-outcomes-title">Hypothetical candidate outcomes</span>} icon="FlaskConical" flush
                  meta={<span>not executed trades</span>}>
                  {outcomes.length === 0 ? (
                    <EmptyState icon="Ban" title="Nothing to settle">No recommended trades to settle.</EmptyState>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table className="sn-table" data-testid="outcomes-table">
                        <thead>
                          <tr><th>Candidate</th><th>Triggered</th><th className="r">Theor. entry</th><th className="r">MFE (SPX pts)</th><th className="r">MAE (SPX pts)</th><th className="r">EOD value</th><th className="r">Hypothetical P&amp;L</th><th>Would have won</th></tr>
                        </thead>
                        <tbody>
                          {outcomes.map((o) => (
                            <tr key={o.id}>
                              <td className="id">{o.candidate_id}</td>
                              <td>{o.triggered ? "Yes" : "No"}</td>
                              <td className="num">{fmt(o.theor_entry, "price")}</td>
                              <td className="num">{fmt(o.mfe, "pts", { signed: true })}</td>
                              <td className="num">{fmt(o.mae, "pts", { signed: true })}</td>
                              <td className="num">{fmt(o.eod_value, "price")}</td>
                              <td className="num">{fmt(o.realized_pnl, "usd", { signed: true })}</td>
                              <td><YesNo v={o.would_have_won} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {outcomes[0]?.display_note && (
                    <p className="sn-small sn-faint" style={{ margin: 0, padding: "var(--space-3) var(--panel-p)", overflowWrap: "anywhere", borderTop: "1px solid var(--border-hairline)" }} data-testid="scoring-source-note">{outcomes[0].display_note}</p>
                  )}
                </Panel>
              </>
            );
          }}
        </QueryBoundary>
      </section>

      <section className="sn-stack" style={{ gap: "var(--space-4)" }} aria-label="Rolling evidence, all sessions" data-testid="scoring-rolling">
        <SectionHead title="Rolling evidence, all sessions" testId="scoring-rolling-head"
          sub="Not tied to the date above. Each panel shows the window it covers in its header." />
        {diag && <Diagnostics diag={diag} />}
        <CostOfNoPanel />
        <PaperStrategiesPanel />
      </section>
    </div>
  );
}
